import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import ProfessionalService from '../services/ProfessionalService';
import { useNavigate, useLocation } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';

// Liste des rôles considérés comme 'professionnel'
const PROFESSIONAL_ROLES = ["PROFESSIONAL", "PROFESSIONNEL", "Employe"];
const PROFILE_SUBMISSION_PATH = "/identification";

const ProfessionalAcceptanceGuard = ({ children }) => {
    const { user, isAuthenticated, isInitialAuthCheck } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [isChecking, setIsChecking] = useState(true);
    const [isAccepted, setIsAccepted] = useState(true);

    // Récupération de l'utilisateur (gère le cas où user est directement l'objet ou imbriqué)
    const currentUser = user?.user || user;

    // CORRECTION 1 : On vérifie role.name car role est un objet (ex: { id: 4, name: "PROFESSIONAL" })
    const isProfessional = isAuthenticated && currentUser?.roles?.some(role =>
        PROFESSIONAL_ROLES.includes(role?.name) || PROFESSIONAL_ROLES.includes(role)
    );

    const isSuperAdmin = currentUser?.roles?.some(role =>
        role?.name === 'SuperAdmin' || role === 'SuperAdmin'
    );

    useEffect(() => {
        // CORRECTION 2 : Ne pas passer la garde si l'auth est en cours de vérification
        if (isInitialAuthCheck) {
            setIsChecking(true);
            return;
        }

        if (!isAuthenticated || isSuperAdmin) {
            setIsChecking(false);
            return;
        }

        if (!isProfessional) {
            setIsChecking(false);
            return;
        }

        const checkAcceptance = async () => {
            if (!currentUser?.useRef) {
                console.error("useRef manquant pour le professionnel.");
                setIsAccepted(false);
                setIsChecking(false);
                return;
            }

            try {
                const accepted = await ProfessionalService.isProfessionalAccepted(currentUser.useRef);
                setIsAccepted(accepted);
            } catch (error) {
                console.error("Échec de la vérification du statut d'acceptation:", error);
                setIsAccepted(false);
            } finally {
                setIsChecking(false);
            }
        };

        checkAcceptance();
    }, [isAuthenticated, isInitialAuthCheck, currentUser, isProfessional, isSuperAdmin]);

    // Afficher le LoadingSpinner pendant la vérification initiale ou l'appel API
    if (isChecking || isInitialAuthCheck) {
        return <LoadingSpinner isVisible={true} loadingText="Vérification du statut professionnel en cours..." />;
    }

    // Si l'utilisateur est sur la page d'identification, on laisse toujours passer
    if (location.pathname === PROFILE_SUBMISSION_PATH) {
        return children;
    }

    // Si c'est un professionnel non accepté, on affiche le message d'accès limité
    if (isProfessional && !isAccepted) {
        // CORRECTION 3 : Utiliser les bons champs du DTO (status et identificationSubmitted)
        const hasSubmittedProfile = currentUser?.status === 'PENDING' ||
            currentUser?.status === 'SUBMITTED' ||
            currentUser?.status === 'UNDER_REVIEW' ||
            currentUser?.identificationSubmitted === true;

        const message = hasSubmittedProfile
            ? "Si vous avez déjà soumis votre dossier, veuillez patienter. La vérification est déjà en cours et peut prendre quelques jours. Vous recevrez une notification dès que votre compte sera validé."
            : "Pour accéder à cette section, vous devez compléter et soumettre votre dossier professionnel. Si vous avez déjà soumis votre dossier, veuillez patienter. La vérification est déjà en cours et peut prendre quelques jours.";

        const buttonText = hasSubmittedProfile ? "Voir mon profil" : "Compléter mon profil";

        return (
            <div style={{
                padding: '40px',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '50vh',
                backgroundColor: '#fafafa'
            }}>
                <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    backgroundColor: hasSubmittedProfile ? '#f0f9ff' : '#f3f4f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '24px'
                }}>
                    <span style={{
                        fontSize: '40px',
                        color: hasSubmittedProfile ? '#0ea5e9' : '#6b7280'
                    }}>
                        {hasSubmittedProfile ? '⏳' : '⏸️'}
                    </span>
                </div>

                <h2 style={{
                    color: '#1f2937',
                    fontSize: '28px',
                    fontWeight: '600',
                    marginBottom: '16px',
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                }}>
                    {hasSubmittedProfile ? 'Validation en cours' : 'Accès temporairement limité'}
                </h2>

                <p style={{
                    color: '#6b7280',
                    fontSize: '16px',
                    lineHeight: '1.6',
                    maxWidth: '500px',
                    marginBottom: '32px',
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                }}>
                    {message}
                </p>

                <button
                    onClick={() => navigate(PROFILE_SUBMISSION_PATH)}
                    style={{
                        background: 'linear-gradient(135deg, #173247 0%, #1d8d36 100%)',
                        border: 'none',
                        borderRadius: '12px',
                        padding: '14px 32px',
                        fontSize: '15px',
                        fontWeight: '500',
                        color: 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                        fontFamily: 'system-ui, -apple-system, sans-serif'
                    }}
                    onMouseOver={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 16px rgba(99, 102, 241, 0.4)';
                    }}
                    onMouseOut={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.3)';
                    }}
                >
                    {buttonText}
                </button>

                {hasSubmittedProfile && (
                    <p style={{
                        color: '#9ca3af',
                        fontSize: '14px',
                        marginTop: '24px',
                        fontStyle: 'italic',
                        maxWidth: '400px'
                    }}>
                        En attendant, vous pouvez modifier votre profil si nécessaire
                    </p>
                )}
            </div>
        );
    }

    // Si l'utilisateur est accepté ou n'est pas un professionnel, on laisse passer
    return children;
};

export default ProfessionalAcceptanceGuard;